import type { ReactNode } from 'react'

import Image from 'next/image'

import { useGsap } from '@/hooks/use-gsap'
import { SplitWords } from '@/components/generals/split-words'
import { commitments } from '@/constants/about'

export const AboutCommitments = (): ReactNode => {
    const scope = useGsap<HTMLElement>(({ gsap, select, scope: root, reduced }) => {
        if (reduced) return

        gsap.from(select('[data-commitments-title] .reveal-mask > span'), {
            yPercent: 115,
            duration: 1.05,
            stagger: 0.06,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 76%' }
        })

        // Cards rise and their photos settle back to scale at the same time.
        select('[data-commitment-card]').forEach((card, index) => {
            const trigger = { trigger: card, start: 'top 87%' }

            gsap.from(card, {
                y: 52,
                opacity: 0,
                duration: 1,
                delay: index * 0.1,
                ease: 'nuvea',
                scrollTrigger: trigger
            })

            gsap.from(card.querySelector('img'), {
                scale: 1.25,
                duration: 1.7,
                delay: index * 0.1,
                ease: 'nuvea',
                scrollTrigger: trigger
            })
        })
    })

    return (
        <section
            ref={scope}
            id='commitments'
            className='bg-shell py-24 md:py-32'
        >
            <div className='shell-x mx-auto w-full max-w-[1760px]'>
                <SplitWords
                    data-commitments-title=''
                    as='h2'
                    lines={['What we promise']}
                    className='type-display text-ink'
                />

                <ul className='mt-12 grid grid-cols-1 gap-8 md:mt-16 md:grid-cols-3 md:gap-6 lg:gap-10'>
                    {commitments.map((commitment) => (
                        <li key={commitment.number} data-commitment-card='' className='flex flex-col'>
                            <div className='relative aspect-[4/5] w-full overflow-hidden rounded-2xl bg-shell'>
                                <Image
                                    src={commitment.image}
                                    alt={commitment.alt}
                                    fill
                                    sizes='(min-width: 768px) 33vw, 100vw'
                                    className='object-cover will-change-transform'
                                />
                            </div>

                            <div className='mt-6 flex items-baseline justify-between gap-4 border-t border-stone-line pt-5'>
                                <h3 className='type-body text-ink'>
                                    {commitment.title}
                                </h3>

                                <span className='type-small text-slate-mute tabular-nums'>
                                    {commitment.number}
                                </span>
                            </div>

                            <p className='type-copy mt-4 max-w-[42ch] text-slate-soft'>
                                {commitment.body}
                            </p>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}
